import React, { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import useCategoriesData from '../../../hooks/useCategoriesData';
import {
  setAdriverSelectedCity,
  setCreationResetButtonIsDisabled,
  setCurrentCity,
  toggleAuthWindow
} from '../../../actions';
import { Photos } from './blocks/Photos';
import {
  BUILDING_TYPE_ID,
  Cities,
  FORM_VALUES,
  INITIAL_FORM_VALUES
} from '../../../utils/helpers/constants';
import { getCategoryValues } from '../../../utils/helpers/common';
import CONFIG from '../../../config';
import { sendAccessToken } from '../../../api/mutations';
import { Button } from '../../ui/Button';
import { ItemCreationLayout } from '../ItemCreationLayout';
import { AddNewItemFormProps } from './types';
import { ButtonType } from '../../../styled/theme/types';
import {
  selectAddNewItemModalIsOpenAuthWindow,
  selectAddNewItemModalResetButtonIsDisabled
} from '../../../store/selectors';
import {
  AuthIframe,
  AuthWrapper,
  ModalContentWrapper,
  resetButtonStyles
} from './styled';
import { TermsAndCondition } from './components/TermsAndCondition';
import useScreenSize from '../../../hooks/useScreenSize';
import { MainFormBlock } from './blocks/MainFormBlock';
import { AboutEstate } from './blocks/AboutEstate';
import { Description } from './blocks/Description';
import { scrollToErrorField } from './scrollToErrorField';
import { Pricing } from './blocks/Pricing';
import { Contacts } from './blocks/Contacts';
import { ErrorPopup } from '../../ErrorPopup';

export const AddNewItemForm: React.FC<AddNewItemFormProps> = ({
  profile,
  errorText,
  categories,
  setErrorText,
  createNewItem,
  failedModalIsOpen,
  toggleFailedModal,
  returnToURL
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset, submit, change } = useForm();
  const { values, errors } = useFormState();
  const { isSmall } = useScreenSize();

  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [citiesLoading, setCitiesIsLoading] = useState(false);

  const [mutateToken] = useMutation(sendAccessToken);

  useCategoriesData({ values, change });

  const creationResetButtonIsDisabled = useSelector(
    selectAddNewItemModalResetButtonIsDisabled
  );
  const isOpenAuthWindow = useSelector(selectAddNewItemModalIsOpenAuthWindow);

  const closeWindow = () => {
    dispatch(toggleAuthWindow(false));
  };

  const validateAndSubmit = async () => {
    await submit();
    if (Object.keys(errors || []).length) {
      scrollToErrorField(isSmall);
    }
  };

  const handleTokenSuccess = (res, token) => {
    if (!res.data?.signIn?.errors?.length) {
      createNewItem({ accessToken: token, values, setLoading });
    } else {
      dispatch(setCreationResetButtonIsDisabled(false));
      setLoading(false);
      toggleFailedModal();
    }
  };

  const handleTokenError = reason => {
    dispatch(setCreationResetButtonIsDisabled(false));
    toggleFailedModal();
    setLoading(false);
    console.error('error in validate auth token', reason);
  };

  const handleToken = token => {
    if (!token) return;
    closeWindow();
    mutateToken({
      variables: { accessToken: token }
    }).then(res => handleTokenSuccess(res, token), handleTokenError);
  };

  const handleAuth = event => {
    if (event.data.name === 'authenticationFinished') {
      setLoading(true);
      dispatch(setCreationResetButtonIsDisabled(true));
      handleToken(event.data.access_token);
    } else if (event.data === 'authenticationModalClose') {
      closeWindow();
      setLoading(false);
      dispatch(setCreationResetButtonIsDisabled(false));
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleAuth);

    return () => window.removeEventListener('message', handleAuth);
  }, [values]);

  const formResetHandler = () => {
    const defaultCategoryId = BUILDING_TYPE_ID.FLAT_ALL;

    const categoryValues = getCategoryValues({
      id: defaultCategoryId,
      categories
    });
    const selectedCategory = categories.find(
      category => category.id === defaultCategoryId
    );

    let resetData = {
      ...INITIAL_FORM_VALUES,
      [FORM_VALUES.categoryId]: BUILDING_TYPE_ID.FLAT_NEW,
      [FORM_VALUES.categoryValues]: categoryValues,
      [FORM_VALUES.typeEstate]: selectedCategory?.name
    };

    if (profile) {
      const { company, phone, email, owner } = values;

      resetData = {
        ...resetData,
        [FORM_VALUES.company]: company,
        [FORM_VALUES.phone]: phone,
        [FORM_VALUES.email]: email,
        ...(profile.targetType === 'AGENCY' && { [FORM_VALUES.owner]: owner })
      };
    }

    dispatch(setCurrentCity(Cities.Baku.id));
    dispatch(setAdriverSelectedCity(Cities.Baku.az));
    reset(resetData);
  };

  const onHideHandleClick = () => {
    if (!loading) {
      const pathname = returnToURL ? new URL(returnToURL).pathname : '/';

      router.replace(pathname);
    }
  };

  const clearErrorAndClose = () => {
    setErrorText('');
    toggleFailedModal();
  };

  const returnToUrl =
    typeof window !== 'undefined' ? btoa(window.location.href) : '';
  const authUrl = `${CONFIG.authAppUrl}/phone-confirmation?phone=${
    values[FORM_VALUES.phone]
  }&return_to=${returnToUrl}`;

  const resetButton = (modalButton = false) => (
    <Button
      text={isSmall ? t('common.reset') : t('new_ad.reset')}
      onClick={formResetHandler}
      state={ButtonType.link}
      disabled={creationResetButtonIsDisabled}
      cypress="item-creation-form-reset-btn"
      stat="item-creation-form-reset-btn"
      styles={resetButtonStyles(modalButton)}
    />
  );

  const termsAndCondition = (
    <TermsAndCondition
      onSubmit={validateAndSubmit}
      isLoading={loading}
      isDisabled={citiesLoading || photoLoading}
      buttonCypress="new-ad-form-submit-button"
    />
  );

  return (
    <ItemCreationLayout
      cypress="item-creation-form"
      onClose={onHideHandleClick}
      contentCypress="item-modal__content"
      isFormStep
      modalHeaderRightBlock={resetButton(true)}
      leftModalBlock={resetButton()}
      isModalHeaderHidden={isOpenAuthWindow}
    >
      <ModalContentWrapper>
        <MainFormBlock
          citiesLoading={citiesLoading}
          setCitiesIsLoading={setCitiesIsLoading}
          categories={categories}
        />
        <AboutEstate />
        <Photos loading={photoLoading} setLoading={setPhotoLoading} />
        <Description />
        <Pricing />
        <Contacts termsBlock={isSmall && termsAndCondition} />
        <ErrorPopup
          isOpen={failedModalIsOpen}
          onClose={clearErrorAndClose}
          title={t('payments.errors.title')}
          message={errorText || t('common.something_went_wrong')}
        />
        {!isSmall && termsAndCondition}
        {isOpenAuthWindow && (
          <AuthWrapper onClick={closeWindow}>
            <AuthIframe
              title="auth"
              src={authUrl}
              onClick={e => e.stopPropagation()}
            />
          </AuthWrapper>
        )}
      </ModalContentWrapper>
    </ItemCreationLayout>
  );
};
